import { pdf } from "@react-pdf/renderer"
import { InventoryReportPdfDocument } from "../../utils/InventoryReportPdfDocument"
import type { MovementControlRow } from "../types/inventoryMovement"
import { computeMovementKpi, formatNumberId } from "./inventoryMovementHelpers"
import {
  buildMovementExportRows,
  MOVEMENT_EXPORT_HEADERS
} from "./inventoryMovementExport.common"

const MOVEMENT_COLUMN_FLEX = [
  2, 4, 5, 6, 6, 3, 3, 4, 4, 4, 4, 4, 4, 4, 8, 6
]

export async function exportMovementPdf(params: {
  periodLabel: string
  rows: MovementControlRow[]
  orientation: "portrait" | "landscape"
  fileName?: string
}): Promise<void> {
  const { periodLabel, rows, orientation, fileName } = params
  const kpi = computeMovementKpi(rows)
  const body = buildMovementExportRows(rows).map((r) =>
    r.map((c) => String(c))
  )

  const kpiHeaders = [
    "Opening",
    "Total In",
    "Total Out",
    "Closing",
    "OK",
    "Warning",
    "Error",
    "Score"
  ]
  const kpiValues = [
    formatNumberId(kpi.openingBalance),
    formatNumberId(kpi.totalIn),
    formatNumberId(kpi.totalOut),
    formatNumberId(kpi.closingBalance),
    String(kpi.okCount),
    String(kpi.warningCount),
    String(kpi.errorCount),
    `${kpi.controlScorePct}%`
  ]

  const columnAlign = MOVEMENT_EXPORT_HEADERS.map((_, i) =>
    [5, 6, 7, 8, 9, 10, 11, 12].includes(i)
      ? ("right" as const)
      : ("left" as const)
  )

  const firstPageRowLimit = orientation === "landscape" ? 14 : 9
  const otherPageRowLimit = orientation === "landscape" ? 26 : 18

  const doc = (
    <InventoryReportPdfDocument
      orientation={orientation}
      documentTitle="Inventory Movement Control"
      heading="Inventory Report"
      subtitleLines={[
        `Inventory Movement / Stock Card Control Report | Period: ${periodLabel}`,
        "Formula: Previous Balance + IN - OUT = Closing Balance"
      ]}
      kpiHeaders={kpiHeaders}
      kpiValues={kpiValues}
      tableHeaders={MOVEMENT_EXPORT_HEADERS}
      tableRows={body}
      columnFlex={MOVEMENT_COLUMN_FLEX}
      columnAlign={columnAlign}
      firstPageRowLimit={firstPageRowLimit}
      otherPageRowLimit={otherPageRowLimit}
    />
  )

  const blob = await pdf(doc).toBlob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName ?? "Inventory_Movement_Control_Report.pdf"
  link.click()
  URL.revokeObjectURL(url)
}
